import { Season } from '../constants';
import {
  GamePredictionRecordsByGameID,
  IAccuracy,
  IAccuracyMetrics,
  IAccuracyMetricsByConfidence,
  IAccuracyMetricsByGameType,
  IAccuracyMetricsBySeason,
  IAccuracyMetricsBySeasonCollection,
  IAccuracyMetricsByWeekCollection,
  IActualGameResult,
  IGamePrediction,
  IMatchup,
  ModelVersionID,
  SeasonWeekID,
  TeamAbrvID,
} from '../types';
import { breakSeasonWeekID, toTeamAbrvID } from './id-helpers';
import { isConferenceMatch } from './team-helpers';

/**
 * Create IActualGameResult from completed game stats and compare to prediction.
 * Determines if prediction was correct and if confidence level was appropriate.
 *
 * @param {IMatchup} game - Matchup that should contain completed game statistics
 * @param {IGamePrediction} prediction - The prediction to validate
 *
 * @return {IActualGameResult} Actual game result with prediction validation
 */
export const createActualResultFromGameStats = (game: IMatchup, prediction: IGamePrediction): IActualGameResult => {
  const homeTeamAbrvID = toTeamAbrvID(game.teamIDHome);
  const awayTeamAbrvID = toTeamAbrvID(game.teamIDAway);
  const homeScore = parseInt(game.homePts ?? '0');
  const awayScore = parseInt(game.awayPts ?? '0');
  const margin = Math.abs(homeScore - awayScore);

  // Determine actual winner
  const actualWinnerTeamAbrvID: TeamAbrvID | 'TIE' =
    game.homeResult === 'W' ? homeTeamAbrvID : game.awayResult === 'W' ? awayTeamAbrvID : 'TIE';
  // Check if prediction was correct
  const predictionCorrect = prediction.predictedWinnerTeamAbrvID === actualWinnerTeamAbrvID;

  // Validate the predicted confidence label against the game's absolute final score margin:
  // high: >=14 points; medium: >=7 and <14 points; low: <7 points.
  // This can be true even when the predicted winner is wrong. It measures margin-band
  // agreement only, not whether the predicted win probability was well calibrated.
  let confidenceValidated = false;
  if (prediction.confidence === 'high' && margin >= 14) {
    confidenceValidated = true;
  } else if (prediction.confidence === 'medium' && margin >= 7 && margin < 14) {
    confidenceValidated = true;
  } else if (prediction.confidence === 'low' && margin < 7) {
    confidenceValidated = true;
  }

  return {
    winnerTeamAbrvID: actualWinnerTeamAbrvID,
    homeScore,
    awayScore,
    margin,
    predictionCorrect,
    confidenceValidated,
  };
};

const initialAccuracy: IAccuracy = {
  totalPredictions: 0,
  correctPredictions: 0,
  accuracyPercentage: 0,
};

type InitialSeasonAccuracyFn = () => IAccuracyMetricsBySeason;
const getInitialSeasonAccuracy: InitialSeasonAccuracyFn = () => ({
  earlySeason: { ...initialAccuracy },
  stableSeason: { ...initialAccuracy },
  fullSeason: { ...initialAccuracy },
});

type InitialSeasonsCollectionAccuracyFn = () => IAccuracyMetricsBySeasonCollection;
const getInitialSeasonsCollection: InitialSeasonsCollectionAccuracyFn = () => {
  const seasonsCollection: IAccuracyMetricsBySeasonCollection = {};
  Object.entries(Season).forEach(([, season]) => {
    seasonsCollection[season as Season] = getInitialSeasonAccuracy();
  });
  return seasonsCollection;
};

/**
 * Calculate accuracy metrics from a set of predictions that have actual results.
 * Only includes predictions that have been validated against actual game outcomes.
 *
 * @param {GamePredictionRecordsByGameID} predictions - All predictions (filtered by version if needed)
 * @param {string} modelVersion - The model version these predictions are for
 *
 * @return {IAccuracyMetrics} Calculated accuracy metrics
 */
export const calculateAccuracyMetrics = (
  predictions: GamePredictionRecordsByGameID,
  modelVersion: string
): IAccuracyMetrics => {
  const predictionsList = Object.values(predictions).filter((p) => p.actualResult !== undefined);

  if (predictionsList.length === 0) {
    // No completed predictions yet - return empty metrics
    return {
      modelVersion,
      accuracyPercentage: 0,
      totalPredictions: 0,
      correctPredictions: 0,
      byConfidence: { high: initialAccuracy, medium: initialAccuracy, low: initialAccuracy },
      byGameType: { division: initialAccuracy, conference: initialAccuracy, interconference: initialAccuracy },
      bySeason: getInitialSeasonsCollection(),
      bySeasonWeek: {},
      byTeam: {},
    };
  }

  // By confidence - Overall metrics
  const byConfidence: IAccuracyMetricsByConfidence = {
    high: { ...initialAccuracy },
    medium: { ...initialAccuracy },
    low: { ...initialAccuracy },
  };

  // By game type - Overall metrics
  const byGameType: IAccuracyMetricsByGameType = {
    division: { ...initialAccuracy },
    conference: { ...initialAccuracy },
    interconference: { ...initialAccuracy },
  };

  const bySeasonWeekCollection: IAccuracyMetricsByWeekCollection = {};
  const bySeasonCollection: IAccuracyMetricsBySeasonCollection = {};

  let totalPredictions = 0;
  let correctPredictions = 0;

  predictionsList.forEach((prediction) => {
    const isCorrect = prediction.actualResult!.predictionCorrect;
    const { numericWeekID } = breakSeasonWeekID(prediction.seasonWeekID);
    // const { awayTeamAbrvID, homeTeamAbrvID } = breakGameID(prediction.gameID);
    const isEarlySeason = numericWeekID <= 3;

    // Create NEW objects for each week (don't reuse references!)
    if (!bySeasonWeekCollection[prediction.seasonWeekID]) {
      bySeasonWeekCollection[prediction.seasonWeekID] = {
        seasonWeekID: prediction.seasonWeekID,
        ...initialAccuracy,
        byGameType: {
          division: { ...initialAccuracy },
          conference: { ...initialAccuracy },
          interconference: { ...initialAccuracy },
        },
        byConfidence: {
          high: { ...initialAccuracy },
          medium: { ...initialAccuracy },
          low: { ...initialAccuracy },
        },
      };
    }
    const byWeek = bySeasonWeekCollection[prediction.seasonWeekID];

    bySeasonCollection[prediction.seasonID] = bySeasonCollection[prediction.seasonID] || getInitialSeasonAccuracy();
    const bySeason = bySeasonCollection[prediction.seasonID];

    // #region
    // const homeTeam = {
    //   ...team,
    //   teamAbrvID: homeTeamAbrvID,
    //   totalPredictions: 1,
    //   correctPredictions: isCorrect ? 1 : 0,
    //   accuracyPercentage: 0,
    //   byConfidence: {
    //     [prediction.confidence]: {
    //       totalPredictions: 1,
    //       correctPredictions: isCorrect ? 1 : 0,
    //       accuracyPercentage: 0,
    //     },
    //   }
    // };
    // const awayTeam = {
    //   ...team,
    //   teamAbrvID: awayTeamAbrvID,
    //   totalPredictions: 1,
    //   correctPredictions: isCorrect ? 1 : 0,
    //   accuracyPercentage: 0,
    //   byConfidence: {
    //     [prediction.confidence]: {
    //       totalPredictions: 1,
    //       correctPredictions: isCorrect ? 1 : 0,
    //       accuracyPercentage: 0,
    //     },
    //   },
    // };
    // #endregion

    totalPredictions++;
    if (isCorrect) correctPredictions++;

    // By confidence
    byConfidence[prediction.confidence].totalPredictions++;
    if (isCorrect) byConfidence[prediction.confidence].correctPredictions++;

    // By season period
    bySeason.fullSeason.totalPredictions++;
    if (isCorrect) bySeason.fullSeason.correctPredictions++;

    if (isEarlySeason) {
      bySeason.earlySeason.totalPredictions++;
      if (isCorrect) bySeason.earlySeason.correctPredictions++;
    } else {
      bySeason.stableSeason.totalPredictions++;
      if (isCorrect) bySeason.stableSeason.correctPredictions++;
    }

    byWeek.seasonWeekID = prediction.seasonWeekID;
    byWeek.totalPredictions++;
    if (isCorrect) byWeek.correctPredictions++;

    // By confidence within week
    byWeek.byConfidence[prediction.confidence].totalPredictions++;
    if (isCorrect) byWeek.byConfidence[prediction.confidence].correctPredictions++;

    // By game type - using contextFactors to determine
    // For now, we'll just track division games, the rest go to "conference" as default
    if (prediction.contextFactors.divisionRivalry) {
      byGameType.division.totalPredictions++;
      byWeek.byGameType.division.totalPredictions++;
      if (isCorrect) {
        byGameType.division.correctPredictions++;
        byWeek.byGameType.division.correctPredictions++;
      }
    } else if (isConferenceMatch(prediction.gameID)) {
      // Default to conference (we don't have data to distinguish conference vs interconference)
      byGameType.conference.totalPredictions++;
      byWeek.byGameType.conference.totalPredictions++;
      if (isCorrect) {
        byGameType.conference.correctPredictions++;
        byWeek.byGameType.conference.correctPredictions++;
      }
    } else {
      byGameType.interconference.totalPredictions++;
      byWeek.byGameType.interconference.totalPredictions++;
      if (isCorrect) {
        byGameType.interconference.correctPredictions++;
        byWeek.byGameType.interconference.correctPredictions++;
      }
    }
  });
  // const msg = `Totals calculated %c ${totalPredictions} %c predictions %c ${correctPredictions} %c correct`;
  // console.log(`${Emo.party} ${msg}`, Cls.Info, '', Cls.Army, '');

  // Calculate percentages
  const calculatePercentage = (correct: number, total: number): number => {
    return total > 0 ? Math.round((correct / total) * 100) : 0;
  };

  Object.entries(bySeasonCollection).forEach(([, seasonMetrics]) => {
    seasonMetrics.fullSeason.accuracyPercentage = calculatePercentage(
      seasonMetrics.fullSeason.correctPredictions,
      seasonMetrics.fullSeason.totalPredictions
    );
    seasonMetrics.earlySeason.accuracyPercentage = calculatePercentage(
      seasonMetrics.earlySeason.correctPredictions,
      seasonMetrics.earlySeason.totalPredictions
    );
    seasonMetrics.stableSeason.accuracyPercentage = calculatePercentage(
      seasonMetrics.stableSeason.correctPredictions,
      seasonMetrics.stableSeason.totalPredictions
    );
  });

  Object.entries(bySeasonWeekCollection).forEach(([, weekMetrics]) => {
    weekMetrics.accuracyPercentage = calculatePercentage(weekMetrics.correctPredictions, weekMetrics.totalPredictions);

    // By confidence
    Object.entries(weekMetrics.byConfidence as IAccuracyMetricsByConfidence).forEach(([, confMetrics]) => {
      confMetrics.accuracyPercentage = calculatePercentage(
        confMetrics.correctPredictions,
        confMetrics.totalPredictions
      );
    });

    // By game type
    Object.entries(weekMetrics.byGameType as IAccuracyMetricsByGameType).forEach(([, typeMetrics]) => {
      typeMetrics.accuracyPercentage = calculatePercentage(
        typeMetrics.correctPredictions,
        typeMetrics.totalPredictions
      );
    });
  });

  // By confidence
  Object.entries(byConfidence as IAccuracyMetricsByConfidence).forEach(([, confMetrics]) => {
    confMetrics.accuracyPercentage = calculatePercentage(confMetrics.correctPredictions, confMetrics.totalPredictions);
  });

  // By game type
  Object.entries(byGameType as IAccuracyMetricsByGameType).forEach(([, typeMetrics]) => {
    typeMetrics.accuracyPercentage = calculatePercentage(typeMetrics.correctPredictions, typeMetrics.totalPredictions);
  });

  return {
    modelVersion,
    accuracyPercentage: calculatePercentage(correctPredictions, totalPredictions),
    totalPredictions,
    correctPredictions,
    byConfidence,
    byGameType,
    bySeason: bySeasonCollection,
    bySeasonWeek: bySeasonWeekCollection,
  };
};

/**
 * Compare accuracy between two model versions.
 * Useful for A/B testing different model configurations.
 *
 * @param {IAccuracyMetrics} version1 - Metrics for first model version
 * @param {IAccuracyMetrics} version2 - Metrics for second model version
 *
 * @return {object} Comparison showing which version performs better in each category
 */
export const compareModelVersions = (
  version1: IAccuracyMetrics,
  version2: IAccuracyMetrics
): {
  overallWinner: string;
  highConfidenceWinner: string;
  earlySeasonWinner: string;
  stableSeasonWinner: string;
  differences: {
    overall: number;
    highConfidence: number;
    earlySeason: number;
    stableSeason: number;
  };
} => {
  const overallDiff = version1.accuracyPercentage - version2.accuracyPercentage;
  const highConfDiff = version1.byConfidence.high.accuracyPercentage - version2.byConfidence.high.accuracyPercentage;
  const earlySeasonDiff =
    version1.bySeason[Season.Season2023].earlySeason.accuracyPercentage -
    version2.bySeason[Season.Season2023].earlySeason.accuracyPercentage;
  const stableSeasonDiff =
    version1.bySeason[Season.Season2023].stableSeason.accuracyPercentage -
    version2.bySeason[Season.Season2023].stableSeason.accuracyPercentage;

  return {
    overallWinner: overallDiff > 0 ? version1.modelVersion : version2.modelVersion,
    highConfidenceWinner: highConfDiff > 0 ? version1.modelVersion : version2.modelVersion,
    earlySeasonWinner: earlySeasonDiff > 0 ? version1.modelVersion : version2.modelVersion,
    stableSeasonWinner: stableSeasonDiff > 0 ? version1.modelVersion : version2.modelVersion,
    differences: {
      overall: Math.abs(overallDiff),
      highConfidence: Math.abs(highConfDiff),
      earlySeason: Math.abs(earlySeasonDiff),
      stableSeason: Math.abs(stableSeasonDiff),
    },
  };
};

/**
 * Aggregate prediction accuracy metrics across all seasons for each model version.
 * Consolidates metrics from multiple seasons/years into cumulative totals per model version,
 * recalculating accuracy percentages based on the combined prediction counts.
 *
 * @param {Record<ModelVersionID, IAccuracyMetrics>} predictionMetricsFromFB - Metrics organized by model version ID from Firebase

 *
 * @return {Record<ModelVersionID, IAccuracyMetrics>} Consolidated metrics with cumulative totals for each model version
 */
export const calculateCumulativeAccuracyMetricsByModelVersionID = (
  predictionMetricsFromFB: Record<ModelVersionID, IAccuracyMetrics>
): Record<ModelVersionID, IAccuracyMetrics> => {
  if (!predictionMetricsFromFB) return predictionMetricsFromFB;

  // need to combine all seasons into one object keyed by model version
  const predictionMetricsByModelVersion: Record<ModelVersionID, IAccuracyMetrics> = {};
  Object.values(predictionMetricsFromFB).forEach((metrics) => {
    if (!predictionMetricsByModelVersion[metrics.modelVersion]) {
      predictionMetricsByModelVersion[metrics.modelVersion] = {
        modelVersion: metrics.modelVersion,
        byConfidence: {
          high: { totalPredictions: 0, correctPredictions: 0, accuracyPercentage: 0 },
          medium: { totalPredictions: 0, correctPredictions: 0, accuracyPercentage: 0 },
          low: { totalPredictions: 0, correctPredictions: 0, accuracyPercentage: 0 },
        },
        byGameType: {
          division: { totalPredictions: 0, correctPredictions: 0, accuracyPercentage: 0 },
          conference: { totalPredictions: 0, correctPredictions: 0, accuracyPercentage: 0 },
          interconference: { totalPredictions: 0, correctPredictions: 0, accuracyPercentage: 0 },
        },
        bySeason: {},
        bySeasonWeek: {},
        totalPredictions: 0,
        correctPredictions: 0,
        accuracyPercentage: 0,
      };
    }

    const existingMetrics = predictionMetricsByModelVersion[metrics.modelVersion];

    // Aggregate metrics
    existingMetrics.totalPredictions += metrics.totalPredictions;
    existingMetrics.correctPredictions += metrics.correctPredictions;
    existingMetrics.accuracyPercentage = (existingMetrics.correctPredictions / existingMetrics.totalPredictions) * 100;

    // By Confidence
    (['high', 'medium', 'low'] as Array<keyof IAccuracyMetrics['byConfidence']>).forEach((level) => {
      existingMetrics.byConfidence[level].totalPredictions += metrics.byConfidence[level].totalPredictions;
      existingMetrics.byConfidence[level].correctPredictions += metrics.byConfidence[level].correctPredictions;
      existingMetrics.byConfidence[level].accuracyPercentage =
        (existingMetrics.byConfidence[level].correctPredictions /
          existingMetrics.byConfidence[level].totalPredictions) *
        100;
    });

    // By Game Type
    (['division', 'conference', 'interconference'] as Array<keyof IAccuracyMetrics['byGameType']>).forEach((type) => {
      existingMetrics.byGameType[type].totalPredictions += metrics.byGameType[type].totalPredictions;
      existingMetrics.byGameType[type].correctPredictions += metrics.byGameType[type].correctPredictions;
      existingMetrics.byGameType[type].accuracyPercentage =
        (existingMetrics.byGameType[type].correctPredictions / existingMetrics.byGameType[type].totalPredictions) * 100;
    });

    // By Season
    Object.entries(metrics.bySeason).forEach(([seasonID, seasonMetrics]) => {
      if (!existingMetrics.bySeason[seasonID]) {
        existingMetrics.bySeason[seasonID] = {
          earlySeason: { totalPredictions: 0, correctPredictions: 0, accuracyPercentage: 0 },
          stableSeason: { totalPredictions: 0, correctPredictions: 0, accuracyPercentage: 0 },
          fullSeason: { totalPredictions: 0, correctPredictions: 0, accuracyPercentage: 0 },
        };
      }
      (['earlySeason', 'stableSeason', 'fullSeason'] as Array<keyof IAccuracyMetricsBySeason>).forEach((period) => {
        existingMetrics.bySeason[seasonID][period].totalPredictions += seasonMetrics[period].totalPredictions;
        existingMetrics.bySeason[seasonID][period].correctPredictions += seasonMetrics[period].correctPredictions;
        existingMetrics.bySeason[seasonID][period].accuracyPercentage =
          (existingMetrics.bySeason[seasonID][period].correctPredictions /
            existingMetrics.bySeason[seasonID][period].totalPredictions) *
          100;
      });
    });

    // By Season Week
    Object.entries(metrics.bySeasonWeek).forEach(([seasonWeekID, weekMetrics]) => {
      if (!existingMetrics.bySeasonWeek[seasonWeekID as SeasonWeekID]) {
        existingMetrics.bySeasonWeek[seasonWeekID as SeasonWeekID] = weekMetrics;
      } else {
        existingMetrics.bySeasonWeek[seasonWeekID as SeasonWeekID].totalPredictions += weekMetrics.totalPredictions;
        existingMetrics.bySeasonWeek[seasonWeekID as SeasonWeekID].correctPredictions += weekMetrics.correctPredictions;
        existingMetrics.bySeasonWeek[seasonWeekID as SeasonWeekID].accuracyPercentage =
          (existingMetrics.bySeasonWeek[seasonWeekID as SeasonWeekID].correctPredictions /
            existingMetrics.bySeasonWeek[seasonWeekID as SeasonWeekID].totalPredictions) *
          100;
      }
    });

    predictionMetricsByModelVersion[metrics.modelVersion] = existingMetrics;
  });

  return predictionMetricsByModelVersion;
};
